import { useUser } from '@clerk/clerk-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState, useRef } from 'react';
import { getRuntimeAuthProvider } from '@/config/auth';
import { clearAuth0SupabaseToken, getFreshAuth0IdTokenClaims } from '@/services/auth0-token';
import { linkAuth0Identity } from '@/services/auth0-identity-link';
import { getSupabaseWithJWT } from '../integrations/supabase/client';
import { openAuthRedirect } from '@/utils/authRedirect';

interface UserProfile {
  id: string;  // This is the UUID from the profiles table
  clerk_user_id: string | null;  // This is the Clerk user ID
  display_name: string | null;
  bio: string | null;
  field_of_study: string | null;
  year: string | null;
  interests: string[] | null;
  avatar_url: string | null;
  student_email?: string | null;
  phone?: string | null;
  created_at: string;
  updated_at: string;
}

const PRIVATE_PROFILE_SELECT = [
  'id',
  'clerk_user_id',
  'display_name',
  'bio',
  'field_of_study',
  'year',
  'interests',
  'avatar_url',
  'student_email',
  'phone',
  'created_at',
  'updated_at',
].join(', ');

const SAFE_PROFILE_SELECT = [
  'id',
  'display_name',
  'bio',
  'field_of_study',
  'year',
  'interests',
  'avatar_url',
  'created_at',
  'updated_at',
].join(', ');

async function fetchProfileBy(
  supabaseClient: any,
  column: 'id' | 'clerk_user_id',
  value: string,
) {
  const privateResult = await supabaseClient
    .from('profiles')
    .select(PRIVATE_PROFILE_SELECT)
    .eq(column, value)
    .maybeSingle();

  if (privateResult.error?.code !== '42501') {
    return privateResult;
  }

  return supabaseClient
    .from('profiles')
    .select(SAFE_PROFILE_SELECT)
    .eq(column, value)
    .maybeSingle();
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export function useAuthUser() {
  const authProvider = getRuntimeAuthProvider();
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn: clerkSignedIn } = useUser();
  const auth0 = useAuth0();
  const auth0UserId = auth0.user?.sub ?? null;
  const isLoaded = authProvider === 'auth0' ? !auth0.isLoading : clerkLoaded;
  const isSignedIn = authProvider === 'auth0' ? auth0.isAuthenticated : clerkSignedIn;
  const user = useMemo(
    () =>
      authProvider === 'auth0' && auth0.user
        ? {
            id: auth0.user.sub,
            fullName: auth0.user.name || null,
            firstName: auth0.user.given_name || auth0.user.name?.split(' ')[0] || null,
            lastName: auth0.user.family_name || null,
            username: auth0.user.nickname || null,
            imageUrl: auth0.user.picture || '',
            primaryEmailAddress: auth0.user.email ? { emailAddress: auth0.user.email } : null,
            emailAddresses: auth0.user.email ? [{ emailAddress: auth0.user.email }] : [],
            phoneNumbers: [],
          }
        : clerkUser,
    [authProvider, auth0.user, clerkUser],
  );
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Refs to prevent excessive API calls
  const hasAttemptedFetch = useRef(false);
  const currentUserId = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    
    if (!isSignedIn || !user) {
      setProfile(null);
      setLoading(false);
      setError(null);
      hasAttemptedFetch.current = true;
      currentUserId.current = null;
      window.__CALONE_AUTH0_USER_ID__ = null;
      return;
    }

    // Only fetch once per user to prevent infinite loops.
    // Latch SYNCHRONOUSLY here (before any await) so that re-renders — the
    // auth0 object gets a new identity every render — don't fire concurrent
    // link-auth0-identity requests before the first one resolves.
    if (currentUserId.current === user.id) {
      return;
    }
    currentUserId.current = user.id;
    hasAttemptedFetch.current = true;

    const fetchOrCreateProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        if (authProvider === 'auth0') {
          // Not auth0.getIdTokenClaims() directly: that returns whatever is
          // cached without checking expiry, so a tab reopened after the ID
          // token's own (short) lifetime elapses would send an already-
          // expired JWT to link-auth0-identity below, get a 401, and (prior
          // to this fix) force a full logout despite the Auth0 session/
          // refresh token still being perfectly valid.
          const claims = await getFreshAuth0IdTokenClaims();
          const token = claims?.__raw;
          if (!token || !claims?.sub) throw new Error('Missing Auth0 ID token');

          // Deduped at module scope, so this runs once per subject even though
          // useAuthUser is mounted by several components at the same time.
          const result = await linkAuth0Identity(claims.sub, token, {
            iss: claims.iss,
            aud: claims.aud,
            email: claims.email,
            email_verified: claims.email_verified,
            exp: claims.exp,
          });

          if (!result.ok) {
            const failure = result as { ok: false; status: number; payload: { error?: string } };
            clearAuth0SupabaseToken();
            window.__CALONE_AUTH0_USER_ID__ = null;
            const isEmailVerificationFailure = failure.status === 401;
            const message = isEmailVerificationFailure
              ? 'Verify your email, then sign in again.'
              : failure.payload?.error || 'Account requires manual reconciliation';
            // Only force a fresh login for the case that's actually fixed by
            // one: an unverified/invalid identity (401). Everything else --
            // a transient backend error (500) or an account genuinely
            // needing manual reconciliation (409, unresolved by re-login) --
            // must not cost the user their otherwise-valid, persisted Auth0
            // session. This is the other half of the "asked to log in again"
            // fix: even a legitimate link-auth0-identity failure shouldn't
            // be treated as "you must re-authenticate."
            if (isEmailVerificationFailure) {
              await auth0.logout({
                logoutParams: {
                  returnTo:
                    window.location.pathname === "/auth/check-email"
                      ? `${window.location.origin}/auth/check-email`
                      : window.location.origin,
                },
                openUrl: openAuthRedirect,
              });
            }
            throw new Error(message);
          }

          window.__CALONE_AUTH0_USER_ID__ = result.userId;

          const supabaseClient = await getSupabaseWithJWT();
          const { data: existingProfile, error: fetchError } = await fetchProfileBy(
            supabaseClient,
            'id',
            result.userId,
          );

          if (fetchError) throw new Error(`Failed to fetch profile: ${fetchError.message}`);
          if (!existingProfile) throw new Error('Linked Auth0 profile was not found');
          setProfile(existingProfile as UserProfile);
          return;
        }

        const supabaseClient = await getSupabaseWithJWT();
        
        // Try to get existing profile by Clerk user ID
        const { data: existingProfile, error: fetchError } = await fetchProfileBy(
          supabaseClient,
          'clerk_user_id',
          user.id,
        );

        if (fetchError) {
          console.error('useAuthUser - Error fetching profile:', fetchError);
          throw new Error(`Failed to fetch profile: ${fetchError.message}`);
        }

        if (existingProfile) {
          setProfile(existingProfile as UserProfile);
        } else {
          
          // Extract student_email as the part before @stud.ntnu.no if applicable
          let studentEmail: string | null = null;
          const email = user.primaryEmailAddress?.emailAddress || null;
          if (email && email.endsWith('@stud.ntnu.no')) {
            studentEmail = email.split('@')[0];
          }

          // Determine display name for email signups
          let displayName = user.fullName || user.firstName;
          if (!displayName && email) {
            // Get part before @
            const namePart = email.split('@')[0];
            // Capitalize first letter and each letter after .
            displayName = namePart
              .split('.')
              .map(part => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' ');
          }
          if (!displayName) displayName = 'Anonymous';

          const { data: claimedProfileId, error: claimError } = await supabaseClient
            .rpc('claim_profile_for_clerk', {
              p_clerk_user_id: user.id,
              p_email: email,
              p_display_name: displayName,
              p_avatar_url: user.imageUrl || null,
              p_student_email: studentEmail,
            });

          if (claimError || !claimedProfileId) {
            console.error('useAuthUser - Error claiming profile:', claimError);
            throw new Error(`Failed to claim profile: ${claimError?.message || 'No profile id returned'}`);
          }

          const { data: claimedProfile, error: claimedFetchError } = await fetchProfileBy(
            supabaseClient,
            'id',
            claimedProfileId,
          );

          if (claimedFetchError) {
            console.error('useAuthUser - Error fetching claimed profile:', claimedFetchError);
            throw new Error(`Failed to fetch claimed profile: ${claimedFetchError.message}`);
          }
          if (!claimedProfile) {
            throw new Error('Claimed Clerk profile was not found');
          }

          setProfile(claimedProfile as UserProfile);
        }
      } catch (err: unknown) {
        console.error('useAuthUser - Error in fetchOrCreateProfile:', err);
        setError(errorMessage(err, 'Failed to load user profile'));

        if (authProvider === 'auth0') {
          setProfile(null);
          return;
        }
        
        // Create fallback mock profile for UI consistency
        let fallbackDisplayName = user.fullName || user.firstName;
        const fallbackEmail = user.primaryEmailAddress?.emailAddress || null;
        if (!fallbackDisplayName && fallbackEmail) {
          const namePart = fallbackEmail.split('@')[0];
          fallbackDisplayName = namePart
            .split('.')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
        }
        if (!fallbackDisplayName) fallbackDisplayName = 'Anonymous';
        const fallbackProfile: UserProfile = {
          id: `temp-${user.id}`,
          clerk_user_id: user.id,
          display_name: fallbackDisplayName,
          bio: null,
          field_of_study: null,
          year: null,
          interests: null,
          avatar_url: user.imageUrl || null,
          student_email: user.primaryEmailAddress?.emailAddress || null,
          phone: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setProfile(fallbackProfile);
      } finally {
        setLoading(false);
        hasAttemptedFetch.current = true;
        currentUserId.current = user.id;
      }
    };

    fetchOrCreateProfile();
  }, [authProvider, user, auth0, auth0UserId, isLoaded, isSignedIn]);

  // Simple refresh function that always fetches the latest profile
  const refreshProfile = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const supabaseClient = await getSupabaseWithJWT();
      const { data: existingProfile, error: fetchError } = await (
        authProvider === 'auth0' && window.__CALONE_AUTH0_USER_ID__
          ? fetchProfileBy(supabaseClient, 'id', window.__CALONE_AUTH0_USER_ID__)
          : fetchProfileBy(supabaseClient, 'clerk_user_id', user.id)
      );
      if (fetchError) throw fetchError;
      if (existingProfile) {
        setProfile(existingProfile as UserProfile);
      }
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to refresh user profile'));
    } finally {
      setLoading(false);
      hasAttemptedFetch.current = true;
      currentUserId.current = user.id;
    }
  };

  return {
    user,
    profile,
    loading,
    error,
    isLoaded,
    isSignedIn,
    refreshProfile
  };
}
